import * as React from 'react';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Spinner } from '@/components/ui/Spinner';
import config from '@/config';
import { AuditLogInsertInput, CustomerInsertInput } from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { DEFAULT_UUID } from '@/models/constants';
import { createAuditLogInput } from '@/utils/audit-log.function';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import {
  buildCustomerSecretOperations,
  createDefaultCustomerSecretDrafts,
  createSecretUpsertDraft,
  stripCustomerSecretValues,
} from '../customerSecretStore';
import {
  createCustomerMutation,
  upsertCustomerS3BucketMutation,
  upsertCustomerSecretMutation,
} from '../graphql/mutation';
import CustomerDetailsForm, {
  CustomerFormMode,
  CustomerFormValues,
} from './CustomerDetailsForm';

interface AddCustomerDialogProps {
  open: boolean;
  onToggleOpen: () => void;
}

export function AddCustomerDialog({
  open,
  onToggleOpen,
}: AddCustomerDialogProps) {
  const navigate = useNavigate();
  const [formMode, setFormMode] = React.useState<CustomerFormMode>('form');
  const [isCreatingCustomer, setIsCreatingCustomer] = React.useState(false);
  const [secretDrafts, setSecretDrafts] = React.useState(
    createDefaultCustomerSecretDrafts(),
  );
  const createS3BucketEnabled = config.customer.createS3BucketEnabled;

  const {
    register,
    control,
    reset,
    setError,
    getValues,
    formState: { isValid },
  } = useForm<CustomerFormValues>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      storagePath: '',
      config: {},
      jsonConfig: {},
    },
  });

  React.useEffect(() => {
    if (formMode) {
      const values = getValues();
      reset({
        name: values.name,
        storagePath: values.storagePath,
        config: {},
        jsonConfig: {},
      });
    }
  }, [formMode, getValues, reset]);

  const { mutateAsync: createCustomer } = useMutation({
    mutationFn: (options: {
      input: CustomerInsertInput;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: createCustomerMutation,
        variables: {
          ...options,
        },
      });
    },
  });

  const handleSubmit = async () => {
    if (isCreatingCustomer) {
      return;
    }

    setIsCreatingCustomer(true);
    const timestamp = new Date().toISOString();
    const formValues = getValues();

    const input: CustomerInsertInput = {
      name: formValues.name,
      config: stripCustomerSecretValues(
        formMode === 'form' ? formValues.config : formValues.jsonConfig,
      ),
      id: uuid(),
      rowVersion: DEFAULT_UUID,
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    try {
      const createResult = await createCustomer({
        input,
        auditLogInput: createAuditLogInput({
          action: AuditLogAction.CREATE_CUSTOMER,
          entities: [
            {
              id: input.id!,
              entity: AuditLogEntityType.CUSTOMER,
            },
          ],
        }),
      });

      const customerId = createResult.insertCustomerOne?.id;
      if (!customerId) {
        setIsCreatingCustomer(false);
        toast.error('Customer created, but unable to find the customer id.', {
          duration: 4000,
        });
        return;
      }

      let hasPartialFailure = false;

      const secretOperations = buildCustomerSecretOperations(secretDrafts, {
        includeDelete: false,
      });

      for (const secretOperation of secretOperations) {
        if (secretOperation.type !== 'upsert') {
          continue;
        }

        try {
          await execute({
            query: upsertCustomerSecretMutation,
            variables: {
              input: {
                customerId,
                pathTail: secretOperation.target.pathTail,
                secretJson: secretOperation.secretJson,
              },
              invalidateCacheInput: {
                customerId,
                pathTail: secretOperation.target.pathTail,
              },
              auditLogInput: createAuditLogInput({
                action: AuditLogAction.UPSERT_CUSTOMER_SECRET,
                entities: [
                  {
                    id: customerId,
                    entity: AuditLogEntityType.CUSTOMER,
                  },
                ],
              }),
            },
          });
        } catch (error) {
          hasPartialFailure = true;
          toast.error('Customer created; secret update failed.', {
            duration: 4000,
            description: error instanceof Error ? error.message : undefined,
          });
          break;
        }
      }

      if (createS3BucketEnabled) {
        try {
          await execute({
            query: upsertCustomerS3BucketMutation,
            variables: {
              customerId,
              auditLogInput: createAuditLogInput({
                action: AuditLogAction.UPDATE_CUSTOMER,
                entities: [
                  {
                    id: customerId,
                    entity: AuditLogEntityType.CUSTOMER,
                  },
                ],
              }),
            },
          });
        } catch {
          hasPartialFailure = true;
          toast.error('Customer created, but S3 bucket creation failed.', {
            duration: 4000,
          });
        }
      }

      if (!hasPartialFailure) {
        toast.success('Customer created successfully!', {
          duration: 3000,
        });
      }
      await navigate(`/customers/${customerId}`);
    } catch {
      setIsCreatingCustomer(false);
      toast.error('Failed to create customer.', {
        duration: 4000,
      });
    }
  };

  const handleToggleOpenState = () => {
    if (isCreatingCustomer) {
      return;
    }

    onToggleOpen();
    setSecretDrafts(createDefaultCustomerSecretDrafts());
  };

  return (
    <Dialog open={open} onOpenChange={handleToggleOpenState}>
      <DialogContent className="sm:max-w-[80vw] lg:max-w-[50vw]">
        <DialogHeader>
          <DialogTitle>Add Customer</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[70vh] gap-6 overflow-auto px-1">
          <CustomerDetailsForm
            formMode={formMode}
            setFormMode={setFormMode}
            register={register}
            creatingCustomer={true}
            control={control}
            setError={setError}
            secretDrafts={secretDrafts}
            onStageSecretUpsert={(targetId, credentials) => {
              setSecretDrafts((t) => ({
                ...t,
                [targetId]: createSecretUpsertDraft(credentials),
              }));
            }}
            onStageSecretDelete={(targetId) => {
              setSecretDrafts((t) => ({
                ...t,
                [targetId]: { action: 'delete' },
              }));
            }}
          />
        </div>
        <DialogFooter>
          <Button
            onClick={() => void handleSubmit()}
            variant={'default'}
            className="md:w-[150px]"
            size={'sm'}
            disabled={!isValid || isCreatingCustomer}
          >
            Create
          </Button>
          <Button
            type="button"
            variant={'outline'}
            className="md:w-[150px]"
            size={'sm'}
            onClick={handleToggleOpenState}
            disabled={isCreatingCustomer}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
      <Dialog open={isCreatingCustomer} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-[420px] [&>button]:hidden"
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Creating Customer</DialogTitle>
            <DialogDescription>
              {createS3BucketEnabled
                ? 'This may take a moment while we provision storage.'
                : 'This may take a moment while we finalize customer setup.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-4">
            <Spinner className="h-10 w-10" />
            <span className="text-muted-foreground text-sm">
              {createS3BucketEnabled
                ? 'Creating customer and S3 bucket...'
                : 'Creating customer...'}
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
